/**
 * `wikiEmoji` — inline mark that wraps slash-inserted emoji glyphs so
 * the editor can render them at a larger font-size without bloating
 * every other inline character.
 *
 * The mark exists purely as a visual hint inside the editor: the
 * paired markdown serializer emits the wrapped text without any
 * surrounding syntax, so a saved + reloaded doc carries the raw
 * Unicode codepoints exactly as before (no `<span>` leakage into the
 * `.md` payload). The trade-off: emoji authored in this session
 * (covered by the mark) render large; emoji round-tripped from
 * markdown render at normal text size. Acceptable for the v1
 * "make slash-inserted emoji bigger" requirement — every freshly
 * inserted emoji gets the visual lift the user asked for, and we
 * keep ADR-003 markdown round-trip safety intact.
 *
 * Why a Mark and not a Node?
 *   - The emoji glyph IS the content (no atom needed); marks attach
 *     to text runs and behave naturally with cursor movement,
 *     selection, and copy / paste.
 *   - Marks survive `insertContent` HTML parsing when the schema
 *     registers `parseHTML` against `span.wiki-emoji` — TipTap's
 *     ProseMirror schema would otherwise strip unknown spans on insert.
 *
 * CSS:
 *   The `.wiki-editor .ProseMirror .wiki-emoji` rule in `global.css`
 *   bumps font-size + tweaks vertical alignment. Scoped to the wiki
 *   surface so card / comment / chat editors don't pick it up.
 */

import { Mark, mergeAttributes } from '@tiptap/react'

/**
 * The markdown storage hook tells `tiptap-markdown` how to round-trip
 * this mark. We emit the inner text without any wrapping syntax, so
 * the on-disk markdown stays clean Unicode. The parser entry is a
 * no-op — there's no markdown syntax to recognize; the mark is only
 * applied at insert time via `insertContent('<span class="wiki-emoji">…')`.
 */
export const WikiEmojiMark = Mark.create({
  name: 'wikiEmoji',

  inclusive: false,

  parseHTML() {
    return [
      {
        tag: 'span.wiki-emoji',
      },
    ]
  },

  renderHTML({ HTMLAttributes }: { HTMLAttributes: Record<string, unknown> }) {
    return ['span', mergeAttributes(HTMLAttributes, { class: 'wiki-emoji' }), 0]
  },

  addStorage() {
    return {
      markdown: {
        serialize: {
          open: '',
          close: '',
          mixable: true,
          // Don't strip whitespace — emoji glyphs occasionally span
          // multiple codepoints (ZWJ sequences) and trimming would
          // corrupt them.
          expelEnclosingWhitespace: false,
        },
        // No parser plugin — there's no markdown syntax for this
        // mark. The mark only appears when the slash command inserts
        // an emoji via `insertContent('<span class="wiki-emoji">…')`.
        parse: {},
      },
    }
  },
})
